import { Button, Calendar, cn, Popover, PopoverContent, PopoverTrigger, PseudoInputButton, PseudoInputIcon, PseudoInputLabel } from '@1trading/react';
import { CalendarDays } from 'lucide-react';
import { type ComponentProps, useState } from 'react';
import { toDateInputValue, toLocalStartOfDay } from '@/utils/date';
import { formatDateOnly } from '@/utils/format';

interface DatePickerProps {
  id?: string;
  /** `YYYY-MM-DD`, or blank for no date */
  value: string;
  onChange: (day: string) => void;
  placeholder?: string;
  /** Shows a button that blanks the date, labelled with this */
  clearLabel?: string;
  disabled?: boolean;
  /** First `YYYY-MM-DD` that may be picked; earlier days are greyed out */
  min?: string;
  /** Last `YYYY-MM-DD` that may be picked; later days are greyed out */
  max?: string;
  /** `dropdown` adds month and year selects, spanning `min` (else 100 years back) to `max` (else the end of this year) */
  captionLayout?: ComponentProps<typeof Calendar>['captionLayout'];
  'aria-invalid'?: boolean;
  className?: string;
}

/**
 * The calendar works in local dates, so the picked day round-trips as the same `YYYY-MM-DD` in any timezone.
 * The trigger height is an arbitrary value because tailwind-merge doesn't recognise the library's `h-(--input-height)`.
 */
export const DatePicker = ({
  id,
  value,
  onChange,
  placeholder = 'Pick a date',
  clearLabel,
  disabled,
  min,
  max,
  captionLayout,
  'aria-invalid': ariaInvalid,
  className,
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);
  const selected = value ? toLocalStartOfDay(value) : undefined;
  const first = min ? toLocalStartOfDay(min) : undefined;
  const last = max ? toLocalStartOfDay(max) : undefined;
  const outOfRange = [...(first ? [{ before: first }] : []), ...(last ? [{ after: last }] : [])];
  // Opens on the selected day, else on the nearest month that has a day to pick.
  const today = new Date();
  const month = selected ?? (first && first > today ? first : last && last < today ? last : undefined);
  const hasDropdowns = captionLayout !== undefined && captionLayout !== 'label';
  const pick = (day: string) => {
    onChange(day);
    setOpen(false);
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <PseudoInputButton aria-invalid={ariaInvalid} className={cn('h-[var(--input-height)] border-input bg-card text-card-foreground', className)} disabled={disabled} id={id}>
          <PseudoInputLabel className={cn(!value && 'text-muted-foreground')}>{selected ? formatDateOnly(selected) : placeholder}</PseudoInputLabel>
          <PseudoInputIcon>
            <CalendarDays />
          </PseudoInputIcon>
        </PseudoInputButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          captionLayout={captionLayout}
          className="bg-transparent"
          defaultMonth={month}
          disabled={outOfRange}
          endMonth={hasDropdowns ? last : undefined}
          mode="single"
          onSelect={(date) => date && pick(toDateInputValue(date))}
          selected={selected}
          startMonth={hasDropdowns ? first : undefined}
        />
        {clearLabel && value ? (
          <div className="border-t border-border p-2">
            <Button className="w-full" onClick={() => pick('')} size="sm" type="button" variant="ghost">
              {clearLabel}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
DatePicker.displayName = 'DatePicker';
